import { useState } from "react";
import { useAppDispatch } from "../../../../../app/hooks";
import { QueryParamAggregation } from "../../../../../services/QueryParamService";
import { copyToClipboard } from "./copyToClipboard";
import { downloadCsv } from "./downloadCsv";
import { toCsv } from "./queryParamsCsv";
import Pager, { pageSlice } from "./Pager";
import QueryParamControls from "./QueryParamControls";
import { loadParamUrls, ownValue, QueryParamFinderState } from "./QueryParamFinderSlice";
import QueryParamSummary from "./QueryParamSummary";
import QueryParamTable from "./QueryParamTable";
import { ParamView, useSortedFilteredParams } from "./useSortedFilteredParams";

export type QueryParamResultsProps = {
  readonly userId: number;
  readonly aggregation: QueryParamAggregation;
  readonly urlState: QueryParamFinderState;
};

const PARAMS_PER_PAGE = 30;
const defaultView: ParamView = { filter: "", sortKey: "urlCount", sortOrder: "desc" };

const useExpandedParams = (userId: number, urlState: QueryParamFinderState) => {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const loadPage = (parameter: string, offset: number): void => {
    dispatch(loadParamUrls({ userId, parameter, offset }));
  };
  const toggle = (parameter: string): void => {
    const next = new Set(expanded);
    if (!next.delete(parameter)) next.add(parameter);
    if (next.has(parameter) && !ownValue(urlState.paramUrls, parameter)) loadPage(parameter, 0);
    setExpanded(next);
  };
  return { expanded, toggle, loadPage };
};

const QueryParamResults = ({ userId, aggregation, urlState }: QueryParamResultsProps) => {
  const [view, setView] = useState(defaultView);
  const [page, setPage] = useState(1);
  const { expanded, toggle, loadPage } = useExpandedParams(userId, urlState);
  const params = useSortedFilteredParams(aggregation.queryParams, view);
  const copyParameters = (): void => copyToClipboard(params.map((p) => p.paramName).join("\n"));

  const exportCsv = (): void => downloadCsv(`query-params-user-${userId}.csv`, toCsv(aggregation.queryParams));

  if (aggregation.queryParams.length === 0) return <p>No query parameters found for this user.</p>;
  return (
    <div>
      <QueryParamSummary aggregation={aggregation} />
      <QueryParamControls
        view={view}
        onViewChange={(next) => {
          setView(next);
          setPage(1);
        }}
        onCopyParameters={copyParameters}
        onExport={exportCsv}
      />
      <QueryParamTable
        params={pageSlice(params, page, PARAMS_PER_PAGE)}
        expanded={expanded}
        urlState={urlState}
        onToggle={toggle}
        onPage={loadPage}
      />
      <Pager page={page} pageSize={PARAMS_PER_PAGE} total={params.length} onPageChange={setPage} />
    </div>
  );
};

export default QueryParamResults;
